import React, { useMemo } from "react";
import { Chip, Stack, Button } from "@mui/material";
import { CustomDetailsList } from "../../../shared";
import { convertDate } from "../../../utils";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment";

const pointPromotionTypes = {
  sales_multiplier: "Sales Multiplier",
  sales_bonus: "Sales Bonus",
};

const cardTypeMap = {
  sponsorship: "Sponsorship Card",
  customer: "Customer Card",
};

const dayLabelMap = {
  0: "Sunday",
  1: "Monday",
  2: "Tuesday",
  3: "Wednesday",
  4: "Thursday",
  5: "Friday",
  6: "Saturday",
};

const getDays = (days) => {
  if (!days) return [];

  if (Array.isArray(days)) {
    return days
      .map((day) => {
        if (typeof day === "number") return dayLabelMap[day] || "";
        if (typeof day === "string" && !Number.isNaN(Number(day))) {
          return dayLabelMap[Number(day)] || day;
        }
        if (typeof day === "string") return day;
        return day?.label || day?.name || day?.day || "";
      })
      .filter(Boolean);
  }

  if (typeof days === "string") {
    return days
      .split(",")
      .map((day) => day.trim())
      .filter(Boolean);
  }

  return [];
};

export const PointPromotionDetailsTab = ({ data, id }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isStoreAdmin = pathname.includes("store-admin");
  const adminPathPrefix = isStoreAdmin ? "/store-admin" : "";

  const handleEdit = () => {
    navigate(
      isStoreAdmin
        ? `/store-admin/point-promotions/edit/${id}`
        : `/point-promotions/edit/${id}`,
    );
  };

  const columns = useMemo(
    () => [
      {
        title: "Promotion Name",
        accessor: (row) => row?.name || "-",
      },
      {
        title: "Promotion Type",
        accessor: (row) => pointPromotionTypes[row?.type] || "-",
      },
      {
        title: "Card Type",
        accessor: (row) => {
          const cardType = row?.card_type;
          if (!cardType) return "-";
          return cardTypeMap[cardType] || cardType;
        },
      },
      {
        title: "Segments",
        accessor: (row) => {
          const segments = row?.segments || [];
          return segments.length > 0 ? (
            <Stack direction="row" gap="8px" flexWrap="wrap">
              {segments.map((segment) => (
                <Chip
                  key={segment?.id || segment?.name}
                  label={segment?.name || "-"}
                  size="small"
                  clickable
                  onClick={() => {
                    if (!segment?.id) return;
                    window.open(
                      `${adminPathPrefix}/customer-segmentation/details/${segment.id}`,
                      "_blank",
                    );
                  }}
                />
              ))}
            </Stack>
          ) : (
            "Applicable for all"
          );
        },
        hidden: (row) => row?.card_type !== "customer",
      },
      {
        title: "Loyalty Groups",
        accessor: (row) => {
          const groups = row?.loyalty_card_groups || [];
          return groups.length > 0 ? (
            <Stack direction="row" gap="8px" flexWrap="wrap">
              {groups.map((group) => (
                <Chip
                  key={group?.id || group?.name}
                  label={group?.name || "-"}
                  size="small"
                  clickable
                  onClick={() => {
                    if (!group?.id) return;
                    window.open(
                      `${adminPathPrefix}/loyalty-cards/sponsorship-group/${group.id}`,
                      "_blank",
                    );
                  }}
                />
              ))}
            </Stack>
          ) : (
            "Applicable for all"
          );
        },
        hidden: (row) => row?.card_type !== "sponsorship",
      },
      {
        title: "Multiplier Value",
        accessor: (row) => row?.incentive ?? "-",
        hidden: (row) => row?.type !== "sales_multiplier",
      },
      {
        title: "Bonus Value",
        accessor: (row) => row?.incentive ?? "-",
        hidden: (row) => row?.type !== "sales_bonus",
      },
      {
        title: "Minimum Cart Value",
        accessor: (row) =>
          row?.min_cart_value === null || row?.min_cart_value === undefined
            ? "-"
            : row.min_cart_value,
      },
      {
        title: "Start Date",
        accessor: (row) =>
          row?.start_date
            ? convertDate({ date: row.start_date, format: "DD MMM YYYY" })
            : "-",
      },
      {
        title: "End Date",
        accessor: (row) =>
          row?.end_date
            ? convertDate({ date: row.end_date, format: "DD MMM YYYY" })
            : "-",
      },
      {
        title: "Days",
        accessor: (row) => {
          const days = getDays(row?.days);
          return days.length > 0 ? (
            <Stack direction="row" gap="8px" flexWrap="wrap">
              {days.map((day, index) => (
                <Chip key={`${day}-${index}`} label={day} size="small" />
              ))}
            </Stack>
          ) : (
            "-"
          );
        },
      },
      {
        title: "Start Time",
        accessor: (row) => row?.start_time ?? "-",
      },
      {
        title: "End Time",
        accessor: (row) => row?.end_time ?? "-",
      },
    ],
    [adminPathPrefix],
  );

  return (
    <CustomDetailsList
      headerRightContent={
        data.status === "draft" && (
          <Button
            variant="text"
            color="primary"
            size="small"
            onClick={handleEdit}
            sx={{ p: "8px 16px" }}
          >
            Edit
          </Button>
        )
      }
      columns={columns}
      data={data}
      title="Details"
      marginBottom="0"
    />
  );
};
